import { useEffect, useState } from 'react';

/**
 * How big the text is, chosen in Settings and kept in this browser. Everything is sized in rem (see index.css), so the one
 * root size scales the whole app. Normal is what index.css sets; Small is the compact look, Large is for people who find
 * the rest small. The guide in Help & Support follows the choice with its own zoom (public/guide/getting-started.html).
 */
export const TEXT_SIZES = [
  { key: 'small', label: 'Small', percent: 81.25 },
  { key: 'normal', label: 'Normal', percent: 93.75 },
  { key: 'large', label: 'Large', percent: 106.25 },
];

const STORAGE_KEY = 'uiTextSize';
const EVENT = 'ibigdo:text-size';

export function getTextSize() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (TEXT_SIZES.some((s) => s.key === saved)) return saved;
  } catch (e) {
    // Storage blocked: the normal size
  }
  return 'normal';
}

/** Put the chosen size on the page. Called once before the app draws, and again when it changes. */
export function applyTextSize(key = getTextSize()) {
  const size = TEXT_SIZES.find((s) => s.key === key) || TEXT_SIZES[1];
  // Normal leaves index.css alone, which keeps the figure in one place
  document.documentElement.style.fontSize = size.key === 'normal' ? '' : `${size.percent}%`;
}

export function setTextSize(key) {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch (e) {
    // The choice then lasts until the page is closed
  }
  applyTextSize(key);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: key }));
}

/** The chosen size, kept up to date when it changes anywhere in the app. */
export function useTextSize() {
  const [size, setSize] = useState(getTextSize);
  useEffect(() => {
    const onChange = (e) => setSize(e.detail || getTextSize());
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);
  return size;
}
